import { Context, Image, notDesktop, styled, Text } from '@obvio/app'
import { RichText } from '@obvio/template'
import { Grid, Section } from '@obvio/ui'

import { CenteredSection } from '../CenteredSection'
import { useCursorContext } from '../Cursor'
import { ReadMoreLink } from '../ReadMoreLink'

import type { Schema } from '@obvio/app'
import type { DeepPick } from '@obvio/utils'
import type { ReactElement } from 'react'

const ImageWrap = styled.div`
  height: 32.3125rem;
  width: 100%;
  position: relative;
`

const ImagesGrid = styled(Grid)`
  width: 100%;
`

const SectionNoBottom = styled(CenteredSection)`
  padding-bottom: 0 !important;
`

const BottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  text-transform: uppercase;
  > span {
    flex: 1;
  }
  > span:first-of-type {
    text-align: left;
  }
  > span:last-of-type {
    text-align: right;
  }
  @media ${notDesktop} {
    > span {
      display: none;
    }
    justify-content: center;
  }
`

export const ARTICLE_SELECT = {
  category: {
    id: true,
    title: true,
  },
  id: true,
  content: true,
  createdAt: true,
  image: true,
  title: true,
} as const

export type Article = DeepPick<Schema['Article'], typeof ARTICLE_SELECT>

export type PostProps = {
  article: Article
  nextArticle: string | null
}

export function Post({ article, nextArticle }: PostProps): ReactElement {
  const { setValue } = useCursorContext()
  return (
    <>
      <Context name="heading">
        <SectionNoBottom>
          <Text tag="h1" as="h3">
            {article.title}
          </Text>
        </SectionNoBottom>
      </Context>
      {article.content.map(
        (
          section: string | { type: string; list?: any[]; columns?: number },
          index: number,
        ) => {
          if (typeof section === 'string') {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <CenteredSection key={index}>
                <RichText value={section} asMap={{ h2: 'h3', h3: 'h4' }} />
              </CenteredSection>
            )
          }
          if (section.type === 'IMAGE') {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <CenteredSection key={index}>
                <ImagesGrid
                  templateColumns={`repeat(${
                    section.columns ?? 1
                  }, minmax(0,1fr))`}
                  gap="medium"
                >
                  {(section.list ?? []).map((image) => (
                    <ImageWrap key={image.name}>
                      <Image img={image} />
                    </ImageWrap>
                  ))}
                </ImagesGrid>
              </CenteredSection>
            )
          }
        },
      )}
      <Section>
        <BottomBar>
          <Text tag="span">Fijewski gallery</Text>
          {nextArticle ? (
            // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              onMouseOver={() => setValue('NEXT')}
              onMouseOut={() => setValue(null)}
            >
              <ReadMoreLink
                color="black"
                length="4rem"
                href={`/blog/${nextArticle}`}
              >
                Read next article
              </ReadMoreLink>
            </div>
          ) : null}
          <Text tag="span">
            {new Date(article.createdAt).toLocaleDateString('en-US')}
          </Text>
        </BottomBar>
      </Section>
    </>
  )
}
